import type { VaultMetricsResponse } from '~~/server/api/vault-metrics.get'
import type { PollingManager } from '../core/pollingManager'
import { withEarlyResponse } from '~/utils/earlyResponse'
import { createPollingManager } from '../core/pollingManager'

export type { VaultMetricsResponse }

export const VAULT_METRICS_KEY = 'vault-metrics'
const VAULT_METRICS_URL = '/api/vault-metrics'

export interface VaultMetricsOptions {
  pollingInterval?: number
  lazy?: boolean
}

let singletonPollingManager: PollingManager | null = null

function getOrCreatePollingManager(pollingInterval: number): PollingManager {
  singletonPollingManager ??= createPollingManager({
    interval: pollingInterval,
    onTick: () => void (async () => {
      try {
        // Loaded on the first tick, so a page that only shows the figures never loads viem.
        const { fetchVaultMetricsOnChain } = await import('@/queries/vault/vaultQueries')
        const data = await fetchVaultMetricsOnChain()
        const cached = useNuxtData<VaultMetricsResponse>(VAULT_METRICS_KEY)
        cached.data.value = data
      }
      catch (e) {
        console.error('[useVaultMetrics] client-side polling error:', e)
      }
    })(),
  })
  return singletonPollingManager
}

export function useVaultMetrics(options: VaultMetricsOptions = {}) {
  const { pollingInterval, lazy = false } = options

  const requestFetch = useRequestFetch()

  // On the landing page the first read takes over the request the page made as it loaded.
  const { data, pending, error, status, refresh, clear } = useAsyncData(
    VAULT_METRICS_KEY,
    (_nuxtApp, { signal }) => withEarlyResponse(VAULT_METRICS_URL, () =>
      requestFetch<VaultMetricsResponse>(VAULT_METRICS_URL, { signal })),
    { server: !lazy, lazy, immediate: !lazy },
  )

  if (pollingInterval != null && pollingInterval > 0 && import.meta.client) {
    onMounted(() => {
      scheduleIdleCallback(() => {
        const manager = getOrCreatePollingManager(pollingInterval)
        manager.subscribe()

        tryOnScopeDispose(() => {
          manager.unsubscribe()
        })
      }, { timeout: 1000 })
    })
  }

  return { data, pending, error, status, refresh, clear }
}
